import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ClipboardList, 
  AlertTriangle, 
  Scale, 
  Users, 
  Clock,
  Calendar, 
  CheckCircle, 
  XCircle,
  Eye,
  Edit,
  Trash2,
  Search,
  Plus,
  TrendingUp,
  FileText,
  MapPin,
  User,
  Building2,
  Phone,
  Mail,
  Activity,
  DollarSign,
  CreditCard,
  PieChart,
  Home,
  LogOut,
  Loader2,
  Image,
  X
} from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';

function ViewTrader() {
  const navigate = useNavigate();
  
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    address: '',
    status: ''
  });

  // API instance with token
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    headers: { 'Accept': 'application/json' }
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Helper function to get image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    
    // If it's already a full URL, return as is
    if (path.startsWith('http')) return path;
    if (path.startsWith('data:')) return path;
    
    // Extract just the filename from the full path
    let filename = path;
    // Handle both forward and backslashes
    if (path.includes('/')) {
      filename = path.split('/').pop();
    }
    if (path.includes('\\')) {
      filename = path.split('\\').pop();
    }
    
    // Return the URL to your Laravel endpoint
    return `http://localhost:8000/Trader/${filename}`;
  };

  // Fetch traders from API
  const fetchTraders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/viewtrader');
      console.log('API Response:', response.data);
      
      // Handle different response structures
      let tradersData = [];
      if (response.data && response.data.data) {
        tradersData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        tradersData = response.data;
      } else if (response.data && response.data.traders) {
        tradersData = response.data.traders;
      }
      
      // Transform data to match component structure
      const formattedTraders = tradersData.map(trader => ({
        id: trader.id,
        name: trader.name || trader.full_name || 'N/A',
        tin: trader.tin || trader.tax_id || 'N/A',
        email: trader.email || 'N/A',
        phone: trader.phone || trader.phone_number || 'N/A',
        business_name: trader.business_name || trader.company_name || 'N/A',
        business_license: trader.business_license || trader.license_number || 'N/A',
        address: trader.address || trader.business_address || 'N/A',
        city: trader.city || 'N/A',
        subcity: trader.subcity || trader.sub_city || 'N/A',
        status: trader.status || 'pending',
        created_at: trader.created_at || trader.registration_date,
        updated_at: trader.updated_at,
        email_verified_at: trader.email_verified_at,
        latitude: trader.latitude || trader.lat,
        longitude: trader.longitude || trader.lng,
        total_inspections: trader.total_inspections || trader.inspections_count || 0,
        violations_count: trader.violations_count || 0,
        compliance_rate: trader.compliance_rate || 100,
        // Add photo fields
        license_photo_path: trader.license_photo_path || null,
        national_id_photo_path: trader.national_id_photo_path || null
      }));
      
      setTraders(formattedTraders);
    } catch (error) {
      console.error('Error fetching traders:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load traders',
        timer: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete trader
  const deleteTrader = async (id) => {
    try {
      await api.delete(`/traders/${id}`);
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Trader has been deleted successfully',
        timer: 2000
      });
      fetchTraders(); // Refresh list
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting trader:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to delete trader'
      });
    }
  };

  // Update trader
  const updateTrader = async () => {
    try {
      const response = await api.put(`/traders/${selectedTrader.id}`, editFormData);
      
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Trader information has been updated',
        timer: 2000
      });
      
      setIsEditModalOpen(false);
      setSelectedTrader(null);
      fetchTraders(); // Refresh list
    } catch (error) {
      console.error('Error updating trader:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update trader'
      });
    }
  };

  useEffect(() => {
    fetchTraders();
  }, []);

  // ────────── STATISTICS ──────────
  const stats = useMemo(() => {
    const total = traders.length;
    const active = traders.filter(t => t.status === 'active').length;
    const pending = traders.filter(t => t.status === 'pending').length;
    const inactive = traders.filter(t => t.status === 'inactive').length;
    const totalViolations = traders.reduce((sum, t) => sum + (t.violations_count || 0), 0);
    const avgCompliance = (traders.reduce((sum, t) => sum + (t.compliance_rate || 0), 0) / total || 0).toFixed(1);
    const highRisk = traders.filter(t => (t.compliance_rate || 100) < 70).length;
    const totalInspections = traders.reduce((sum, t) => sum + (t.total_inspections || 0), 0);
    const verifiedTraders = traders.filter(t => t.email_verified_at).length;
    
    // Get new traders this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const newThisMonth = traders.filter(t => {
      if (!t.created_at) return false;
      const createdDate = new Date(t.created_at);
      return createdDate.getMonth() === currentMonth && 
             createdDate.getFullYear() === currentYear;
    }).length;
    
    return { 
      total, 
      active, 
      pending,
      inactive,
      totalViolations,
      avgCompliance,
      highRisk,
      totalInspections,
      verifiedTraders,
      newThisMonth
    };
  }, [traders]);

  const filteredTraders = traders.filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.tin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openViewModal = (trader) => {
    setSelectedTrader(trader);
    setIsViewModalOpen(true);
  };

  const handleAddTrader = () => {
    navigate('/admin/addtrader');
  };

  const openEditModal = (trader) => {
    setSelectedTrader(trader);
    setEditFormData({
      name: trader.name || '',
      email: trader.email || '',
      phone: trader.phone || '',
      business_name: trader.business_name || '',
      address: trader.address || '',
      status: trader.status || 'pending'
    });
    setIsEditModalOpen(true);
  };

  const openPhotoModal = (photoUrl, photoType) => {
    setSelectedPhoto({ url: photoUrl, type: photoType });
    setIsPhotoModalOpen(true);
  };

  const handleDelete = (id) => {
    if (deleteConfirmId === id) {
      deleteTrader(id);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const handleUpdate = () => {
    updateTrader();
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get compliance rate color
  const getComplianceColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };



  
   if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative inline-block">
          {/* Spinner */}
          <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-700 rounded-full animate-spin border-t-transparent"></div>
          
          {/* Inner icon */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="w-6 h-6 text-indigo-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        
        <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading Your Data</h3>
        <p className="mt-2 text-gray-500">Please wait while we fetch your data...</p>
        
        {/* Loading steps */}
        <div className="mt-6 space-y-2">
          {['Fetching inspections', 'Loading statistics', 'Preparing charts'].map((step, index) => (
            <div key={step} className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <div className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-indigo-600 animate-pulse' : 'bg-gray-300'}`}></div>
              <span className={index === 0 ? 'text-indigo-600' : ''}>{step}</span>
              {index === 0 && (
                <svg className="w-3 h-3 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="w-full sm:w-auto">
              <div className="flex items-center gap-2 md:gap-3">
                <Users className="h-8 w-8 md:h-10 md:w-10 text-indigo-600 flex-shrink-0" />
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Traders Management
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 mt-1">View and manage all registered traders</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleAddTrader}
              className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2.5 md:px-6 md:py-3 rounded-xl font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
            >
              <Plus className="h-5 w-5" />
              Add New Trader
            </button>
          </div>
        </div>

        {/* Statistics Cards - First Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-3 md:mb-4">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-4 md:p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Traders</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
                <p className="text-xs text-indigo-600 mt-1">+{stats.newThisMonth} this month</p>
              </div>
              <div className="bg-indigo-100 rounded-full p-2 md:p-3 flex-shrink-0 ml-2">
                <Users className="h-5 w-5 md:h-6 md:w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-4 md:p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs md:text-sm font-medium">Active Traders</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.active}</p>
                <p className="text-xs text-green-600 mt-1">{((stats.active / stats.total) * 100 || 0).toFixed(1)}% of total</p>
              </div>
              <div className="bg-green-100 rounded-full p-2 md:p-3 flex-shrink-0 ml-2">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-4 md:p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs md:text-sm font-medium">Avg Compliance Rate</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.avgCompliance}%</p>
                <p className="text-xs text-yellow-600 mt-1">{stats.highRisk} high risk traders</p>
              </div>
              <div className="bg-yellow-100 rounded-full p-2 md:p-3 flex-shrink-0 ml-2">
                <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-4 md:p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Inspections</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.totalInspections}</p>
                <p className="text-xs text-purple-600 mt-1">{stats.totalViolations} violations found</p>
              </div>
              <div className="bg-purple-100 rounded-full p-2 md:p-3 flex-shrink-0 ml-2">
                <ClipboardList className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards - Second Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-3 md:p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs font-medium">Pending</p>
                <p className="text-xl md:text-2xl font-bold text-gray-800">{stats.pending}</p>
              </div>
              <div className="bg-orange-100 rounded-full p-1.5 md:p-2 flex-shrink-0 ml-2">
                <Clock className="h-4 w-4 md:h-5 md:w-5 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-3 md:p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs font-medium">Inactive</p>
                <p className="text-xl md:text-2xl font-bold text-gray-800">{stats.inactive}</p>
              </div>
              <div className="bg-red-100 rounded-full p-1.5 md:p-2 flex-shrink-0 ml-2">
                <XCircle className="h-4 w-4 md:h-5 md:w-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-3 md:p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs font-medium">Verified</p>
                <p className="text-xl md:text-2xl font-bold text-gray-800">{stats.verifiedTraders}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-1.5 md:p-2 flex-shrink-0 ml-2">
                <Mail className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-md p-3 md:p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-cyan-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-xs font-medium">High Risk</p>
                <p className="text-xl md:text-2xl font-bold text-gray-800">{stats.highRisk}</p>
              </div>
              <div className="bg-cyan-100 rounded-full p-1.5 md:p-2 flex-shrink-0 ml-2">
                <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-cyan-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative w-full max-w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-4 h-4 md:w-5 md:h-5" />
            <input
              type="text"
              placeholder="Search by name, TIN, email or business name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 md:pl-10 pr-4 py-2 md:py-2.5 border-2 border-indigo-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white text-sm md:text-base"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1024px] md:min-w-full w-full divide-y divide-gray-200">
              <thead className="bg-indigo-700">
                <tr>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Trader Name</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">TIN</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden lg:table-cell">Business Name</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Contact</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Documents</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Inspections</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Compliance</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTraders.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No traders found</p>
                     </td>
                   </tr>
                ) : (
                  filteredTraders.map((item) => (
                    <tr key={item.id} className="hover:bg-indigo-50 transition-colors duration-150">
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-gray-900 text-xs md:text-sm">{item.id}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap font-medium text-gray-900 text-xs md:text-sm">{item.name}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap font-mono text-gray-700 text-xs md:text-sm">{item.tin}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-gray-700 text-xs md:text-sm hidden lg:table-cell">{item.business_name}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4">
                        <div className="text-xs md:text-sm text-gray-700">
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            <span>{item.phone}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[120px]">{item.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {item.license_photo_path && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.license_photo_path), 'Business License')}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                              title="View Business License"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                          {item.national_id_photo_path && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.national_id_photo_path), 'National ID')}
                              className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                              title="View National ID"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                          {!item.license_photo_path && !item.national_id_photo_path && (
                            <span className="text-gray-400 text-xs">No documents</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center hidden sm:table-cell">
                        <span className="text-sm font-semibold text-gray-900">{item.total_inspections}</span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                item.compliance_rate >= 80 ? 'bg-green-600' : 
                                item.compliance_rate >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${item.compliance_rate}%` }}
                            />
                          </div>
                          <span className={`text-sm font-semibold ${getComplianceColor(item.compliance_rate)}`}>
                            {item.compliance_rate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden sm:table-cell">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-right text-xs md:text-sm font-medium">
                        <div className="flex justify-end space-x-1 md:space-x-2">
                          <button
                            onClick={() => openViewModal(item)}
                            className="p-1.5 md:p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-1.5 md:p-1 text-blue-600 hover:text-blue-800 transition rounded-lg hover:bg-blue-100"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className={`p-1.5 md:p-1 transition rounded-lg ${
                              deleteConfirmId === item.id
                                ? 'bg-red-600 text-white px-2 py-1.5 md:px-2 md:py-1'
                                : 'text-red-600 hover:text-red-800 hover:bg-red-100'
                            }`}
                            title={deleteConfirmId === item.id ? 'Confirm Delete' : 'Delete'}
                          >
                            <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Detail Modal */}
        {isViewModalOpen && selectedTrader && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-xl md:rounded-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    Trader Details
                  </h2>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Trader Name</p>
                    <p className="text-sm sm:text-base font-medium text-gray-900">{selectedTrader.name}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">TIN Number</p>
                    <p className="text-sm sm:text-base font-mono font-medium">{selectedTrader.tin}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Email</p>
                    <p className="text-sm sm:text-base">{selectedTrader.email}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Phone</p>
                    <p className="text-sm sm:text-base">{selectedTrader.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Business Name</p>
                    <p className="text-sm sm:text-base font-medium">{selectedTrader.business_name}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Business License</p>
                    <p className="text-sm sm:text-base">{selectedTrader.business_license}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs sm:text-sm text-gray-500">Address</p>
                    <p className="text-sm sm:text-base flex items-center gap-2">
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                      {selectedTrader.address}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Status</p>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedTrader.status)}`}>
                      {selectedTrader.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Registration Date</p>
                    <p className="text-sm sm:text-base">{new Date(selectedTrader.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Total Inspections</p>
                    <p className="text-sm sm:text-base font-semibold">{selectedTrader.total_inspections}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Violations</p>
                    <p className="text-sm sm:text-base text-red-600 font-semibold">{selectedTrader.violations_count}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Compliance Rate</p>
                    <p className={`text-sm sm:text-base font-semibold ${getComplianceColor(selectedTrader.compliance_rate)}`}>
                      {selectedTrader.compliance_rate}%
                    </p>
                  </div>
                </div>

                {/* Photos Section in View Modal */}
                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">Documents / Photos</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedTrader.license_photo_path && (
                      <div className="text-center">
                        <button
                          onClick={() => openPhotoModal(getImageUrl(selectedTrader.license_photo_path), 'Business License')}
                          className="w-full"
                        >
                          <img 
                            src={getImageUrl(selectedTrader.license_photo_path)} 
                            alt="Business License"
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x200?text=License+Not+Found';
                            }}
                          />
                          <p className="text-xs text-gray-600 mt-2">Business License</p>
                        </button>
                      </div>
                    )}
                    {selectedTrader.national_id_photo_path && (
                      <div className="text-center">
                        <button
                          onClick={() => openPhotoModal(getImageUrl(selectedTrader.national_id_photo_path), 'National ID')}
                          className="w-full"
                        >
                          <img 
                            src={getImageUrl(selectedTrader.national_id_photo_path)} 
                            alt="National ID"
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x200?text=ID+Not+Found';
                            }}
                          />
                          <p className="text-xs text-gray-600 mt-2">National ID</p>
                        </button>
                      </div>
                    )}
                    {!selectedTrader.license_photo_path && !selectedTrader.national_id_photo_path && (
                      <p className="text-gray-400 text-sm col-span-2 text-center py-4">No documents uploaded</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Link
                    to={`/admin/traders/${selectedTrader.id}/inspections`}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                  >
                    View Inspections
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedTrader && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-xl md:rounded-2xl w-full max-w-2xl sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Edit Trader Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Trader Name</label>
                    <input
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleEditChange}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={editFormData.email}
                      onChange={handleEditChange}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleEditChange}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Business Name</label>
                    <input
                      type="text"
                      name="business_name"
                      value={editFormData.business_name}
                      onChange={handleEditChange}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={editFormData.address}
                      onChange={handleEditChange}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <select 
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditChange}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 sm:mt-8">
                    <button
                      onClick={() => setIsEditModalOpen(false)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm sm:text-base"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Modal for Fullscreen View */}
        {isPhotoModalOpen && selectedPhoto && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl w-full">
              <button
                onClick={() => setIsPhotoModalOpen(false)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
              >
                <X size={32} />
              </button>
              <div className="bg-white rounded-xl overflow-hidden">
                <div className="p-4 bg-gray-100 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">{selectedPhoto.type}</h3>
                </div>
                <div className="p-4 flex justify-center">
                  <img 
                    src={selectedPhoto.url} 
                    alt={selectedPhoto.type}
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    }}
                  />
                </div>
                <div className="p-4 bg-gray-50 flex justify-end">
                  <button
                    onClick={() => setIsPhotoModalOpen(false)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewTrader;